import { useEffect, useMemo, useState } from 'react';
import type { Loan, LoanEvent, RepaymentConsequence } from '@/types';
import { CurrencyInput } from '@/components/CurrencyInput';
import { computeRepaymentBasis, type RepaymentBasis } from '@/lib/calc/repaymentBasis';
import { formatInr } from '@/lib/format';
import {
  initialStateForNewEvent,
  initialStateForExistingConsequence,
  recalculateFromBasis,
  applyManualEmiEdit,
  applyManualTenureEdit,
  type ConsequenceDisplayState,
} from './repaymentConsequenceState';

interface RepaymentConsequenceFieldsProps {
  loan: Loan;
  /** Every currently-active event for the loan (unfiltered — this component applies the chronological-exclusion rule itself via computeRepaymentBasis). */
  events: LoanEvent[];
  /** The effective date of the event being created/edited — determines the basis (see computeRepaymentBasis). */
  eventEffectiveDate: string;
  /**
   * Optional adjustment applied to the basis principal AFTER it is
   * computed from history — used only by PrepaymentConsequenceFields,
   * which passes the negative of the prepayment amount so the
   * suggestion reflects the principal AFTER this specific reduction
   * (the prepayment event itself isn't in `events` yet, since it hasn't
   * been saved, so computeRepaymentBasis alone can't know about it).
   * Ignored (0) for RateChangeEvent/BalanceCorrectionEvent, where the
   * basis from history alone is already correct.
   */
  principalAdjustment?: number;
  /**
   * For a RATE CHANGE event only: the new rate currently being typed
   * into the form (not yet saved). When supplied, the basis calculation
   * uses this rate for BOTH the displayed "applicable rate" and the
   * forward projection that determines remaining tenure, so the
   * suggestion recalculates live as the user changes the rate — per the
   * approved requirement. Omitted entirely for prepayment/balance-
   * correction, which don't change the rate.
   */
  liveRateOverride?: number;
  value: RepaymentConsequence | undefined;
  onChange: (next: RepaymentConsequence) => void;
}

/**
 * The mandatory repayment-consequence sub-form shared by RateChangeEvent
 * and BalanceCorrectionEvent (PrepaymentEvent uses a thin wrapper,
 * PrepaymentConsequenceFields, that adds its own `intent` field around
 * the same core behaviour, passing `principalAdjustment`).
 *
 * STATE MACHINE (approved spec — see repaymentConsequenceState.ts for the
 * pure, independently-tested logic this component wires up to the UI):
 *
 *   NEW EVENT: while the user has not yet manually edited either field,
 *   BOTH EMI and remaining tenure stay LIVE — they keep recalculating
 *   from the current basis every time the basis changes (new rate typed,
 *   prepayment amount changed, etc.). Neither is labelled "Set by you"
 *   during this phase, even though a calculation direction (tenure ->
 *   EMI) is used internally.
 *
 *   The moment the user edits EITHER field directly, that field becomes
 *   the primary lever and is NEVER again overwritten by a basis change —
 *   only the OTHER field keeps recalculating from that point forward.
 *   This is what prevents ping-pong.
 *
 *   EXISTING EVENT: a previously saved consequence loads as-is and is
 *   treated as already user-edited — no live recalculation happens on
 *   mount merely because the current basis differs from what was true
 *   when it was originally saved.
 */
export function RepaymentConsequenceFields({
  loan,
  events,
  eventEffectiveDate,
  principalAdjustment = 0,
  liveRateOverride,
  value,
  onChange,
}: RepaymentConsequenceFieldsProps) {
  const basis: RepaymentBasis = useMemo(() => {
    const raw = computeRepaymentBasis(loan, events, eventEffectiveDate, liveRateOverride);
    return {
      ...raw,
      outstandingPrincipal: Math.max(0, raw.outstandingPrincipal + principalAdjustment),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loan, events, eventEffectiveDate, principalAdjustment, liveRateOverride]);

  // The "Keep current EMI" checkbox — an explicit user CHOICE that
  // changes which lever the AUTOMATIC (not-yet-manually-edited) phase
  // holds fixed. Deliberately a SEPARATE piece of state from
  // manualEditState: toggling this checkbox is NEVER itself a manual
  // edit of EMI or tenure, so it must never set hasUserManuallyEdited.
  // Default unchecked, matching the approved default behaviour (tenure
  // fixed, EMI recalculates).
  const [keepCurrentEmi, setKeepCurrentEmi] = useState(false);

  // The display state is derived FRESH on every render from (a) whether
  // this is a new or existing consequence, and (b) the current basis —
  // via useMemo, not useState+useEffect. This sidesteps the exact defect
  // that caused the original bug (a one-shot effect that could never
  // re-fire once the parent-held `value` prop became defined): here,
  // there is no "has this effect already run" guard tied to the parent
  // prop at all. Instead, hasUserManuallyEdited — tracked in LOCAL state,
  // set true only by an actual user edit — is the sole gate for whether
  // live recalculation continues, exactly as required.
  const [manualEditState, setManualEditState] = useState<ConsequenceDisplayState | undefined>(
    value?.newEmi !== undefined || value?.newRemainingTenureMonths !== undefined
      ? initialStateForExistingConsequence({
          newEmi: value?.newEmi,
          newRemainingTenureMonths: value?.newRemainingTenureMonths,
          primaryLever: value?.primaryLever ?? 'tenure',
        })
      : undefined
  );

  const displayState: ConsequenceDisplayState = useMemo(() => {
    const base = manualEditState ?? initialStateForNewEvent();
    return recalculateFromBasis(base, basis, keepCurrentEmi, basis.currentActiveEmi);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualEditState, basis.outstandingPrincipal, basis.applicableAnnualRatePercent, basis.remainingTenureMonths, basis.currentActiveEmi, keepCurrentEmi]);

  const [status, setStatus] = useState<RepaymentConsequence['status']>(value?.status ?? 'projected');
  const [repaymentTermsEffectiveDate, setRepaymentTermsEffectiveDate] = useState(
    value?.repaymentTermsEffectiveDate ?? eventEffectiveDate
  );

  function commit(state: ConsequenceDisplayState, nextStatus = status, nextDate = repaymentTermsEffectiveDate) {
    onChange({
      newEmi: state.emi,
      newRemainingTenureMonths: state.tenure,
      primaryLever: state.primaryLever,
      status: nextStatus,
      repaymentTermsEffectiveDate: nextDate,
    });
  }

  function handleEmiChange(next: number | undefined) {
    setManualEditState(applyManualEmiEdit(next, basis));
  }

  function handleTenureChange(next: number | undefined) {
    setManualEditState(applyManualTenureEdit(next, basis));
  }

  function handleStatusChange(next: RepaymentConsequence['status']) {
    setStatus(next);
    commit(displayState, next);
  }

  function handleDateChange(next: string) {
    setRepaymentTermsEffectiveDate(next);
    commit(displayState, status, next);
  }

  // Whenever the LIVE (not-yet-manually-edited) display state changes as
  // a result of a basis change, propagate it up to the parent so the
  // event's draft data stays in sync with what's shown — without this,
  // the parent would only learn about the live suggestion once the user
  // finally made a manual edit, leaving a stale/empty consequence if they
  // saved the form before touching either field.
  //
  // CRITICAL: this effect depends ONLY on values derived from LOCAL state
  // (displayState.emi/tenure/primaryLever, hasUserManuallyEdited) and the
  // basis itself — never on the parent-held `value` prop. This is
  // precisely what avoids the original defect, where a one-shot effect
  // gated on `value` could never re-fire once the parent's state was
  // first populated. Committing here is idempotent (the parent simply
  // receives the same shape of data each time) and safe to run on every
  // relevant change.
  useEffect(() => {
    commit(displayState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayState.emi, displayState.tenure, displayState.primaryLever, displayState.hasUserManuallyEdited]);

  return (
    <div className="stack">
      <div className="card" style={{ borderColor: 'var(--color-accent)' }}>
        <div className="card-title">Repayment Consequence</div>
        <p style={{ marginTop: 0 }}>
          Based on a projected outstanding principal of {formatInr(basis.outstandingPrincipal, true)} at{' '}
          {basis.applicableAnnualRatePercent.toFixed(2)}%, with {basis.remainingTenureMonths} EMI cycles remaining
          under your current repayment trajectory, here is the calculated effect on your EMI and remaining tenure.
          Edit either value directly — the other recalculates automatically.
        </p>

        <div className="field-group">
          <label>
            <input
              type="checkbox"
              checked={keepCurrentEmi}
              disabled={displayState.hasUserManuallyEdited}
              onChange={(e) => setKeepCurrentEmi(e.target.checked)}
              style={{ width: 'auto', marginRight: 8 }}
            />
            Keep current EMI
          </label>
          <span className="field-hint">
            {displayState.hasUserManuallyEdited
              ? 'Not applicable once you have manually edited EMI or tenure below.'
              : keepCurrentEmi
                ? `EMI stays at your current ${formatInr(basis.currentActiveEmi, true)}; remaining tenure recalculates.`
                : 'Default: remaining tenure stays fixed at the current trajectory; EMI recalculates.'}
          </span>
        </div>

        <div className="row">
          <div className="field-group" style={{ marginBottom: 0 }}>
            <label htmlFor="consequenceEmi">
              EMI{' '}
              {displayState.hasUserManuallyEdited && displayState.primaryLever === 'emi' && (
                <span className="badge badge-actual_lic">Set by you</span>
              )}
              {(!displayState.hasUserManuallyEdited || displayState.primaryLever === 'tenure') && (
                <span className="badge badge-projected">Calculated</span>
              )}
            </label>
            <CurrencyInput id="consequenceEmi" value={displayState.emi} onChange={handleEmiChange} />
          </div>
          <div className="field-group" style={{ marginBottom: 0 }}>
            <label htmlFor="consequenceTenure">
              Remaining Tenure (months){' '}
              {displayState.hasUserManuallyEdited && displayState.primaryLever === 'tenure' && (
                <span className="badge badge-actual_lic">Set by you</span>
              )}
              {(!displayState.hasUserManuallyEdited || displayState.primaryLever === 'emi') && (
                <span className="badge badge-projected">Calculated</span>
              )}
            </label>
            <input
              id="consequenceTenure"
              type="number"
              inputMode="numeric"
              value={displayState.tenure ?? ''}
              onChange={(e) => handleTenureChange(e.target.value ? Number(e.target.value) : undefined)}
            />
          </div>
        </div>

        <div className="field-group">
          <label htmlFor="consequenceStatus">Status</label>
          <select
            id="consequenceStatus"
            value={status}
            onChange={(e) => handleStatusChange(e.target.value as RepaymentConsequence['status'])}
          >
            <option value="projected">Projected (what I currently believe will happen)</option>
            <option value="actual_lic">LIC-Confirmed (from an actual statement/letter)</option>
          </select>
          <span className="field-hint">
            Start with Projected, then come back and edit this same event to switch it to LIC-Confirmed once you know
            the actual outcome.
          </span>
        </div>

        <div className="field-group" style={{ marginBottom: 0 }}>
          <label htmlFor="repaymentTermsEffectiveDate">Repayment Terms Effective Date</label>
          <input
            id="repaymentTermsEffectiveDate"
            type="date"
            value={repaymentTermsEffectiveDate}
            onChange={(e) => handleDateChange(e.target.value)}
          />
          <span className="field-hint">
            The date the revised EMI/tenure actually takes effect — may differ from this event's own effective date.
          </span>
        </div>
      </div>
    </div>
  );
}
